'use client';

import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import Autoplay, { type AutoplayType } from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MouseEventHandler,
} from 'react';

export type HeroSlideImageSet = {
  desktop?: string;
  tablet?: string;
  mobile?: string;
};

export type HeroSlide = {
  images: HeroSlideImageSet;
  alt?: string;
  link?: string;
  buttonName?: string;
};

export type HeroSliderProps = {
  slides: HeroSlide[];
  options?: EmblaOptionsType;
  autoplayDelay?: number;
  className?: string;
  style?: CSSProperties;
};

type ButtonHandler = MouseEventHandler<HTMLButtonElement>;

const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (api: EmblaCarouselType) => void,
) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const updateStates = useCallback((api: EmblaCarouselType) => {
    setPrevBtnDisabled(!api.canScrollPrev());
    setNextBtnDisabled(!api.canScrollNext());
  }, []);

  const onPrevButtonClick = useCallback<ButtonHandler>(
    (event) => {
      event.preventDefault();
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollPrev();
      onButtonClick?.(emblaApi);
    },
    [emblaApi, onButtonClick],
  );

  const onNextButtonClick = useCallback<ButtonHandler>(
    (event) => {
      event.preventDefault();
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollNext();
      onButtonClick?.(emblaApi);
    },
    [emblaApi, onButtonClick],
  );

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    updateStates(emblaApi);
    emblaApi.on('reInit', updateStates).on('select', updateStates);

    return () => {
      emblaApi.off('reInit', updateStates).off('select', updateStates);
    };
  }, [emblaApi, updateStates]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

const useDotButton = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (api: EmblaCarouselType) => void,
) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollTo(index);
      onButtonClick?.(emblaApi);
    },
    [emblaApi, onButtonClick],
  );

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handleSelect = (api: EmblaCarouselType) => {
      setSelectedIndex(api.selectedScrollSnap());
    };

    const handleReInit = (api: EmblaCarouselType) => {
      setScrollSnaps(api.scrollSnapList());
      handleSelect(api);
    };

    handleReInit(emblaApi);

    emblaApi.on('reInit', handleReInit).on('select', handleSelect);

    return () => {
      emblaApi.off('reInit', handleReInit).off('select', handleSelect);
    };
  }, [emblaApi]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

const PrevButton: FC<{
  disabled: boolean;
  onClick: ButtonHandler;
}> = ({ disabled, onClick }) => (
  <button
    className="embla__button embla__button--prev"
    type="button"
    aria-label="Предыдущий слайд"
    disabled={disabled}
    onClick={onClick}
  >
    <svg className="embla__button__svg" viewBox="0 0 532 532">
      <path
        fill="currentColor"
        d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
      />
    </svg>
  </button>
);

const NextButton: FC<{
  disabled: boolean;
  onClick: ButtonHandler;
}> = ({ disabled, onClick }) => (
  <button
    className="embla__button embla__button--next"
    type="button"
    aria-label="Следующий слайд"
    disabled={disabled}
    onClick={onClick}
  >
    <svg className="embla__button__svg" viewBox="0 0 532 532">
      <path
        fill="currentColor"
        d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
      />
    </svg>
  </button>
);

const DotButton: FC<{
  isActive: boolean;
  onClick: () => void;
}> = ({ isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Перейти к слайду"
    className={`embla__dot${isActive ? ' embla__dot--selected' : ''}`}
  />
);

export const HeroSlider: FC<HeroSliderProps> = ({
  slides,
  options,
  autoplayDelay = 5000,
  className = '',
  style,
}) => {
  const autoplayRef = useRef<AutoplayType>(
    Autoplay({
      delay: autoplayDelay,
    }),
  );

  useEffect(() => {
    autoplayRef.current.options.delay = autoplayDelay;
  }, [autoplayDelay]);

  const plugins = useMemo(() => [autoplayRef.current], []);

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const onNavButtonClick = useCallback((api: EmblaCarouselType) => {
    const autoplay = api.plugins()?.autoplay as AutoplayType | undefined;
    if (!autoplay) {
      return;
    }
    const resetOrStop =
      autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop;
    resetOrStop();
  }, []);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi, onNavButtonClick);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  return (
    <section className={`embla ${className}`} style={style}>
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide, index) => {
            const isClickable = Boolean(slide.link);
            const slideContent = (
              <div className="embla__slide__content">
                {slide?.images?.mobile && (
                  <Image
                    src={slide.images.mobile}
                    alt={slide.alt || `Слайд ${index + 1}`}
                    fill
                    className="block md:hidden"
                    style={{ objectFit: 'cover' }}
                    priority={index === 0}
                  />
                )}
                {slide?.images?.tablet && (
                  <Image
                    src={slide.images.tablet}
                    alt={slide.alt || `Слайд ${index + 1}`}
                    fill
                    className="hidden md:block lg:hidden"
                    style={{ objectFit: 'cover' }}
                    priority={index === 0}
                  />
                )}
                {slide?.images?.desktop && (
                  <Image
                    src={slide.images.desktop}
                    alt={slide.alt || `Слайд ${index + 1}`}
                    fill
                    className="hidden lg:block"
                    style={{ objectFit: 'cover' }}
                    priority={index === 0}
                  />
                )}

                {slide.buttonName && (
                  <div
                    className={`absolute bottom-4 left-4 z-10${
                      isClickable ? ' pointer-events-none' : ''
                    }`}
                  >
                    <span className="inline-block rounded-md bg-white/90 px-4 py-2 text-sm text-black transition md:text-base">
                      {slide.buttonName}
                    </span>
                  </div>
                )}
              </div>
            );

            return (
              <div className="embla__slide" key={index}>
                {slide.link ? (
                  <a
                    href={slide.link}
                    className="embla__slide__link"
                    aria-label={slide.alt || `Слайд ${index + 1}`}
                  >
                    {slideContent}
                  </a>
                ) : (
                  slideContent
                )}
              </div>
            );
          })}
        </div>

        <div className="embla__buttons">
          <PrevButton disabled={prevBtnDisabled} onClick={onPrevButtonClick} />
          <NextButton disabled={nextBtnDisabled} onClick={onNextButtonClick} />
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              isActive={index === selectedIndex}
              onClick={() => onDotButtonClick(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSlider;
